import { subscriptionsApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import { ApiError } from '../identity/types';
import type { SubscriptionGateway } from './SubscriptionGateway';
import type {
  BillingHistoryItem, BillingStatusResponse, CheckoutSessionResponse, InvoiceDetail, InvoiceSummary, Page,
  PlanChangePreview, PlanChangeRequest, PortalSessionResponse, ReactivateResult, SubscriptionAccessResult,
  SupportRequestCase, SupportRequestInput,
} from './billingTypes';
import type {
  BillingAccountResponse, CompanySubscriptionResponse, FeatureSummary, PlanSummary,
  SubscriptionChangeRequestResponse, SubscriptionEstimateResponse,
} from './types';

const post = (body?: unknown): RequestInit => ({ method: 'POST', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

/** A list endpoint may answer with a bare array or `{ items, ... }` — accept both rather than guess one. The paging
 * numbers the server omitted are derived from what it did send, never invented. */
function toPage<T>(raw: unknown, requested: { page: number; pageSize: number }): Page<T> {
  if (Array.isArray(raw)) {
    return { items: raw as T[], page: requested.page, pageSize: requested.pageSize, totalCount: raw.length };
  }
  const record = (raw ?? {}) as Partial<Page<T>>;
  const items = record.items ?? [];
  return {
    items,
    page: record.page ?? requested.page,
    pageSize: record.pageSize ?? requested.pageSize,
    totalCount: record.totalCount ?? (requested.page - 1) * requested.pageSize + items.length,
  };
}

type RawCase = Partial<SupportRequestCase> & { caseNumber: string };

function normalizeCase(raw: RawCase): SupportRequestCase {
  return {
    id: raw.id ?? raw.caseNumber,
    caseNumber: raw.caseNumber,
    issueType: raw.issueType ?? 'Other',
    invoiceNumber: raw.invoiceNumber ?? null,
    message: raw.message ?? '',
    status: raw.status ?? 'Open',
    billingContactEmail: raw.billingContactEmail ?? null,
    createdAtUtc: raw.createdAtUtc ?? '',
    updatedAtUtc: raw.updatedAtUtc ?? null,
  };
}

export class HttpSubscriptionGateway implements SubscriptionGateway {
  private readonly request = createJsonClient(subscriptionsApiBaseUrl);

  listPlans(signal?: AbortSignal): Promise<PlanSummary[]> {
    return this.request('/api/subscription-plans', undefined, signal);
  }

  listFeatures(signal?: AbortSignal): Promise<FeatureSummary[]> {
    return this.request('/api/subscription-features', undefined, signal);
  }

  async getCurrentSubscription(signal?: AbortSignal): Promise<CompanySubscriptionResponse | null> {
    try {
      return await this.request('/api/subscription', undefined, signal);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  }

  getEstimate(signal?: AbortSignal): Promise<SubscriptionEstimateResponse> {
    return this.request('/api/subscription/estimate', undefined, signal);
  }

  async getBillingAccount(signal?: AbortSignal): Promise<BillingAccountResponse | null> {
    try {
      return await this.request('/api/subscription/billing-account', undefined, signal);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  }

  updateBillingAccount(input: { billingContactName: string; billingContactEmail: string }): Promise<BillingAccountResponse> {
    return this.request('/api/subscription/billing-account', { method: 'PUT', body: JSON.stringify(input) });
  }

  listChangeHistory(signal?: AbortSignal): Promise<SubscriptionChangeRequestResponse[]> {
    return this.request('/api/subscription/change-requests', undefined, signal);
  }

  requestChange(input: { toPlanCode: string; billingOwnership: string; idempotencyKey: string }): Promise<SubscriptionChangeRequestResponse> {
    return this.request('/api/subscription/change-requests', post(input));
  }

  callDemoFeature(featureKey: 'priority-report-access' | 'advanced-analytics-access'): Promise<{ message: string }> {
    const route = featureKey === 'priority-report-access' ? 'premium-insight' : 'advanced-insight';
    return this.request(`/api/subscription/demo/${route}`);
  }

  // ---- EPN-105 billing -------------------------------------------------------------------------------------

  getAccess(signal?: AbortSignal): Promise<SubscriptionAccessResult> {
    return this.request('/api/subscription/access', undefined, signal);
  }

  async startTrial(input: { planCode: string; idempotencyKey: string }): Promise<void> {
    await this.request('/api/billing/trial', post(input));
  }

  getBillingStatus(signal?: AbortSignal): Promise<BillingStatusResponse> {
    return this.request('/api/billing/status', undefined, signal);
  }

  createCheckoutSession(input: { planCode: string; idempotencyKey: string }): Promise<CheckoutSessionResponse> {
    return this.request('/api/billing/checkout-sessions', post(input));
  }

  getPlanChangePreview(planCode: string, signal?: AbortSignal): Promise<PlanChangePreview> {
    return this.request(`/api/billing/plan-change-preview?planCode=${encodeURIComponent(planCode)}`, undefined, signal);
  }

  async changePlan(input: PlanChangeRequest): Promise<void> {
    await this.request('/api/billing/plan-change', post(input));
  }

  async cancelSubscription(input: { idempotencyKey: string }): Promise<void> {
    await this.request('/api/billing/cancel', post(input));
  }

  async reactivateSubscription(input: { idempotencyKey: string }): Promise<ReactivateResult> {
    // The server answers { outcome, cancelAtPeriodEnd, currentPeriodEndUtc, pendingPlanCode, checkoutSessionId?, checkoutUrl? };
    // outcome CheckoutRequired means the subscription had already ended, so the customer must pay again via checkout.
    const result = await this.request<{ checkoutSessionId?: string; checkoutUrl?: string } | undefined>('/api/billing/reactivate', post(input));
    const checkout = result && typeof result.checkoutUrl === 'string' && result.checkoutUrl.length > 0
      ? { sessionId: result.checkoutSessionId ?? '', url: result.checkoutUrl }
      : null;
    return { checkout };
  }

  createPortalSession(): Promise<PortalSessionResponse> {
    return this.request('/api/billing/portal-sessions', post());
  }

  async listInvoices(input: { page: number; pageSize: number }, signal?: AbortSignal): Promise<Page<InvoiceSummary>> {
    const raw = await this.request<unknown>(`/api/billing/invoices?page=${input.page}&pageSize=${input.pageSize}`, undefined, signal);
    return toPage<InvoiceSummary>(raw, input);
  }

  getInvoice(invoiceId: string, signal?: AbortSignal): Promise<InvoiceDetail> {
    return this.request(`/api/billing/invoices/${encodeURIComponent(invoiceId)}`, undefined, signal);
  }

  async listBillingHistory(input: { page: number; pageSize: number }, signal?: AbortSignal): Promise<Page<BillingHistoryItem>> {
    const raw = await this.request<unknown>(`/api/billing/history?page=${input.page}&pageSize=${input.pageSize}`, undefined, signal);
    return toPage<BillingHistoryItem>(raw, input);
  }

  async createSupportRequest(input: SupportRequestInput): Promise<SupportRequestCase> {
    const created = await this.request<RawCase>('/api/billing/support-requests', post(input));
    return normalizeCase(created);
  }

  async listSupportRequests(signal?: AbortSignal): Promise<SupportRequestCase[]> {
    const raw = await this.request<unknown>('/api/billing/support-requests', undefined, signal);
    const items = Array.isArray(raw) ? raw : ((raw as { items?: unknown[] } | undefined)?.items ?? []);
    return (items as RawCase[]).map(normalizeCase);
  }
}
